import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import LmsAssetUpload from "./lmsAssetUpload";
import LmsAssetRenderer from "./lmsAssetRenderer";
import { assetIcon, assetIdOf, formatDuration } from "../../admin/_lmsHelpers";
import VideoCaptionTrack from "./videoCaptionTrack";

const formatAssetSize = (asset = {}) => {
  const raw =
    asset.sizeBytes ??
    asset.fileSizeBytes ??
    asset.fileSize ??
    asset.size ??
    asset.sizeMb;
  const value = Number(raw);

  if (!value) return null;
  if (asset.sizeMb || value < 1000) return `${value.toFixed(value >= 10 ? 0 : 1)} MB`;

  const mb = value / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;

  const kb = value / 1024;
  return `${kb.toFixed(kb >= 10 ? 0 : 1)} KB`;
};

const assetCopyForAccept = (accept = "", t) => {
  const normalized = accept.toLowerCase();
  const acceptsVideo = normalized.includes("video");
  const acceptsPdf = normalized.includes("pdf");
  const acceptsImage = normalized.includes("image");

  if (acceptsVideo && !acceptsPdf && !acceptsImage) {
    return {
      icon: "fas fa-video",
      empty: t("admin.lms.asset.emptyVideo", "No video yet."),
      upload: t("admin.lms.asset.uploadVideo", "Upload Video"),
    };
  }

  if (acceptsPdf && !acceptsVideo && !acceptsImage) {
    return {
      icon: "fas fa-file-pdf",
      empty: t("admin.lms.asset.emptyPdf", "No PDF yet."),
      upload: t("admin.lms.asset.uploadPdf", "Upload PDF"),
    };
  }

  if (acceptsImage && !acceptsVideo && !acceptsPdf) {
    return {
      icon: "fas fa-image",
      empty: t("admin.lms.asset.emptyImage", "No image yet."),
      upload: t("admin.lms.asset.uploadImage", "Upload Image"),
    };
  }

  return {
    icon: "fas fa-file-upload",
    empty: t("admin.lms.asset.empty"),
    upload: t("admin.lms.asset.upload"),
  };
};

// Fixed-slot uploader for the audience/area-wrapped asset arrays:
//   course intro  → one per serviceAudience (ESHS / SSHS)
//   support assets → one per serviceArea (ESSEX / SUFFOLK)
// Each value gets exactly one slot: an asset card if present, else an uploader.
//   items: [{ _id, [wrapKey]: value, asset }]
//   onAdd(value, asset) · onRemove(wrapperId)
const LmsAudienceAssets = (props) => {
  const { t } = useTranslation();
  const {
    values = [],
    items = [],
    wrapKey,
    labelFor,
    onAdd,
    onRemove,
    accept,
    variant,
    uploadContext,
  } = props;
  const [previewAsset, setPreviewAsset] = useState(null);

  const find = (v) => items.find((it) => it?.[wrapKey] === v);
  const uploadCopy = assetCopyForAccept(accept, t);
  const isCourseIntro = variant === "courseIntro";
  const isLocalSupport = variant === "localSupport";

  const uploadContextFor = (value) => {
    const base =
      typeof uploadContext === "function" ? uploadContext(value) : uploadContext || {};
    if (isCourseIntro) {
      return { assetRole: "course-intro", serviceAudience: value, ...base };
    }
    if (isLocalSupport) {
      return { assetRole: "local-support", serviceArea: value, ...base };
    }
    return { [wrapKey]: value, ...base };
  };

  const introTitleFor = (v) =>
    t(
      `admin.lms.courseIntro.${v}.title`,
      `${labelFor ? labelFor(v) : v} Service Overview`
    );

  const introDescriptionFor = (v) =>
    t(
      `admin.lms.courseIntro.${v}.description`,
      `Plays before learners begin the ${labelFor ? labelFor(v) : v} course modules.`
    );

  const introEmptyFor = (v) =>
    t(
      `admin.lms.courseIntro.${v}.empty`,
      `Upload ${labelFor ? labelFor(v) : v} service overview video`
    );

  const areaLabelFor = (v) => (labelFor ? labelFor(v) : v);

  const supportTitleFor = (v) =>
    t(
      `admin.lms.localSupport.${v}.title`,
      `${areaLabelFor(v)} Local Support PDF`
    );

  const supportDescriptionFor = (v) =>
    t(
      `admin.lms.localSupport.${v}.description`,
      `The PDF learners in ${areaLabelFor(v)} will receive after completing this module.`
    );

  const supportEmptyFor = (v) =>
    t(
      `admin.lms.localSupport.${v}.empty`,
      `Upload ${areaLabelFor(v)} support PDF`
    );

  const supportUploadLabelFor = (v) =>
    t(
      `admin.lms.localSupport.${v}.upload`,
      `Upload ${areaLabelFor(v)} PDF`
    );

  return (
    <>
      {isLocalSupport && (
        <div className="lms-local-support-info">
          <i className="fas fa-info-circle"></i>
          <span>
            {t(
              "admin.lms.localSupport.info",
              "Upload one Local Support Services PDF for each service area."
            )}
          </span>
        </div>
      )}
      <div
        className={`lms-audience-grid ${
          isCourseIntro ? "lms-intro-video-grid" : ""
        } ${isLocalSupport ? "lms-local-support-grid" : ""}`}
      >
        {values.map((v) => {
          const entry = find(v);
          const asset = entry?.asset;
          const src = asset?.previewUrl || asset?.signedUrl || asset?.url;
          const sizeText = formatAssetSize(asset);

          if (isCourseIntro) {
            return (
              <div className="lms-intro-video-card" key={v}>
                <div className="lms-intro-video-head">
                  <span className="lms-intro-video-number">
                    {values.indexOf(v) + 1}
                  </span>
                  <div className="lms-intro-video-title-wrap">
                    <div className="lms-intro-video-title">
                      {introTitleFor(v)}
                    </div>
                    <div className="lms-intro-video-desc">
                      {introDescriptionFor(v)}
                    </div>
                  </div>
                  {/* <span className="lms-required-badge">
                    {t("common.required", "Required")}
                  </span> */}
                  {asset && (
                    <span className="lms-uploaded-badge">
                      {t("admin.lms.asset.uploaded", "Uploaded")}
                      <i className="fas fa-check-circle"></i>
                    </span>
                  )}
                </div>

                {asset ? (
                  <>
                    <div className="lms-intro-video-preview">
                      {asset.type === "video" && src ? (
                        <video
                          src={src}
                          controls
                          preload="metadata"
                          controlsList="nodownload"
                          onPlay={(e) => {
                            document.querySelectorAll("video").forEach((video) => {
                              if (video !== e.target) video.pause();
                            });
                          }}
                        >
                          <VideoCaptionTrack asset={asset} />
                        </video>
                      ) : (
                        <span className="lms-asset-icon">
                          <i className={assetIcon(asset.type)}></i>
                        </span>
                      )}
                    </div>
                    <div className="lms-intro-file-row">
                      <div className="lms-intro-file-name">
                        <i className="fas fa-file-video"></i>
                        <span>{asset.title || asset.filename || "-"}</span>
                      </div>
                      <div className="lms-intro-file-actions">
                        {sizeText && <span>{sizeText}</span>}
                        <button
                          type="button"
                          className="btn btn-xs text-danger lms-intro-delete-btn"
                          onClick={() => onRemove?.(assetIdOf(entry))}
                          aria-label={t("common.delete")}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="lms-intro-video-empty">
                    <span className="lms-upload-placeholder">
                      <i className={uploadCopy.icon}></i>
                      <span>{introEmptyFor(v)}</span>
                    </span>
                    <LmsAssetUpload
                      accept={accept}
                      label={uploadCopy.upload}
                      uploadTitle={introTitleFor(v)}
                      uploadContext={uploadContextFor(v)}
                      onUploaded={(a) => onAdd?.(v, a)}
                    />
                  </div>
                )}

                {/* <div className="lms-intro-video-meta">
                  <span>
                    <i className="far fa-file-video"></i>
                    {t("admin.lms.courseIntro.format", "Format: MP4, WebM or MOV")}
                  </span>
                  <span>
                    {t("admin.lms.courseIntro.maxSize", "Max size: 500MB")}
                  </span>
                  <span>
                    {t(
                      "admin.lms.courseIntro.maxDuration",
                      "Max duration: 20 minutes"
                    )}
                  </span>
                </div> */}
              </div>
            );
          }

          if (isLocalSupport) {
            return (
              <div className="lms-local-support-card" key={v}>
                <div className="lms-local-support-head">
                  <span className="lms-local-support-number">
                    {values.indexOf(v) + 1}
                  </span>
                  <div className="lms-local-support-title-wrap">
                    <div className="lms-local-support-title">
                      {supportTitleFor(v)}
                    </div>
                    <div className="lms-local-support-desc">
                      {supportDescriptionFor(v)}
                    </div>
                  </div>
                  <span
                    className={`lms-local-support-badge ${
                      asset ? "is-uploaded" : "is-needed"
                    }`}
                  >
                    {asset
                      ? t("admin.lms.asset.uploaded", "Uploaded")
                      : t("admin.lms.asset.optional", "Optional")}
                    <i
                      className={
                        asset ? "fas fa-check-circle" : "far fa-circle"
                      }
                    ></i>
                  </span>
                </div>

                {asset ? (
                  <div className="lms-local-support-file">
                    <span className="lms-local-support-file-icon">
                      <i className="fas fa-file-pdf"></i>
                    </span>
                    <div className="lms-local-support-file-main">
                      <strong>{asset.title || asset.filename || "-"}</strong>
                      <span>
                        {t(`admin.lms.module.type_${asset.type}`, asset.type)}
                        {sizeText ? ` - ${sizeText}` : ""}
                      </span>
                    </div>
                    <div className="lms-local-support-file-actions">
                      {src && (
                        <button
                          type="button"
                          className="btn btn-xs lms-local-support-action-btn"
                          onClick={() => setPreviewAsset(asset)}
                          aria-label={t("admin.lms.preview.openFull")}
                        >
                          <i className="fas fa-expand"></i>
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-xs text-danger lms-local-support-delete-btn"
                        onClick={() => onRemove?.(assetIdOf(entry))}
                        aria-label={t("common.delete")}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="lms-local-support-empty">
                    <span className="lms-local-support-empty-icon">
                      <i className={uploadCopy.icon}></i>
                    </span>
                    <div className="lms-local-support-empty-copy">
                      <strong>{supportEmptyFor(v)}</strong>
                      {/* <span>
                        {t(
                          "admin.lms.localSupport.recommended",
                          "Recommended: PDF (Max. 50MB)"
                        )}
                      </span> */}
                    </div>
                    <LmsAssetUpload
                      accept={accept}
                      label={supportUploadLabelFor(v)}
                      uploadTitle={supportTitleFor(v)}
                      uploadContext={uploadContextFor(v)}
                      onUploaded={(a) => onAdd?.(v, a)}
                    />
                  </div>
                )}
              </div>
            );
          }

          return (
            <div className="lms-audience-slot" key={v}>
              <div className="lms-audience-head">
                {labelFor ? labelFor(v) : v}
              </div>
              {asset ? (
                <div className="lms-audience-preview-card">
                  <div className="lms-audience-preview-media">
                    {asset.type === "video" && src ? (
                        <video
                          src={src}
                          controls
                          preload="metadata"
                          controlsList="nodownload"
                          onPlay={(e) => {
                            document.querySelectorAll("video").forEach((video) => {
                              if (video !== e.target) {
                                video.pause();
                              }
                            });
                          }}
                        >
                          <VideoCaptionTrack asset={asset} />
                        </video>
                    ) : asset.type === "image" && src ? (
                      <img src={src} alt="" />
                    ) : (
                      <span className="lms-asset-icon">
                        <i className={assetIcon(asset.type)}></i>
                      </span>
                    )}
                  </div>
                  <div className="lms-audience-preview-footer">
                    <div className="lms-asset-main">
                      <span className="lms-asset-title">
                        {asset.title || "-"}
                      </span>
                      <span className="lms-asset-meta">
                        {t(`admin.lms.module.type_${asset.type}`, asset.type)}
                        {formatDuration(asset.durationSeconds)
                          ? ` · ${formatDuration(asset.durationSeconds)}`
                          : ""}
                      </span>
                    </div>
                    <div className="lms-asset-actions">
                      {src && (
                        <button
                          type="button"
                          className="btn btn-xs"
                          onClick={() => setPreviewAsset(asset)}
                          aria-label={t("admin.lms.preview.openFull")}
                        >
                          <i className="fas fa-expand"></i>
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-xs text-danger"
                        onClick={() => onRemove?.(assetIdOf(entry))}
                        aria-label={t("common.delete")}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="lms-audience-upload-empty">
                  <span className="lms-upload-placeholder">
                    <i className={uploadCopy.icon}></i>
                    <span>{uploadCopy.empty}</span>
                  </span>
                  <LmsAssetUpload
                    accept={accept}
                    label={uploadCopy.upload}
                    uploadTitle={labelFor ? labelFor(v) : v}
                    uploadContext={uploadContextFor(v)}
                    onUploaded={(a) => onAdd?.(v, a)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {previewAsset && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.65)" }}
          role="dialog"
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h5 className="modal-title">
                  {previewAsset.title || t("admin.lms.preview.preview")}
                </h5>
                <button
                  type="button"
                  className="close btn"
                  onClick={() => setPreviewAsset(null)}
                  style={{ fontSize: "1.5rem" }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <LmsAssetRenderer asset={previewAsset} showHeader={false} />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn secondary-btn"
                  onClick={() => setPreviewAsset(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LmsAudienceAssets;
